<template>
  <v-app id="AssetXP">
    <nav-drw
      v-if="loggedIn"
      v-bind:drawer="drawer"
      v-on:closeDrawer="drawer = false"
    />
    <app-bar v-on:openDrawer="drawer = !drawer" />
    <v-main class="brand-background">
      <v-layout fill-height column>
        <v-flex>
          <!-- <message-banner
            variant="warning"
            title="Image Search Temporarily Unavailable"
            message="Our image search feature is currently unavailable. We're working hard to get it back online as soon as possible and will update you once it's available again. Thank you for your patience!"
          /> -->
          <transition name="fade" mode="out-in">
            <!--<keep-alive>-->
            <router-view v-if="routeIsCallback || isHomeRoute || loggedIn"></router-view>
            <!--</keep-alive>-->
          </transition>
        </v-flex>
        <v-flex shrink>
          <inventory-drawer v-if="inventoryActive"> </inventory-drawer>
        </v-flex>
      </v-layout>
    </v-main>

    <v-dialog
      v-model="contextWarningOpen"
      max-width="520"
      persistent
    >
      <v-card class="context-warning-card">
        <v-card-title class="context-warning-title">
          <v-icon class="mr-2 context-warning-icon">mdi-information-outline</v-icon>
          Context Changed
        </v-card-title>
        <v-card-text class="context-warning-text">{{ contextWarningMessage }}</v-card-text>
        <v-card-actions class="pa-6 justify-end">
          <v-btn
            class="context-warning-btn"
            color="#064287"
            elevation="2"
            @click="contextWarningOpen = false"
          >
            Got It
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import AppBar from "./shared/components/AppBar";
import NavDrw from "./components/navigation/NavDrw";
import InventoryDrawer from "./components/InventoryDrawer";
// import MessageBanner from "./shared/components/MessageBanner";
import { mapState } from "vuex";
import { mapActions } from "vuex";

const IS_DEV = process.env.NODE_ENV === "development";
function debugLog() {
  if (!IS_DEV) return;
  console.log.apply(console, arguments);
}

export default {
  components: { AppBar, NavDrw, InventoryDrawer },
  computed: {
    loggedIn() {
      return this.$store.state.authentication.loggedIn;
    },

    routeIsCallback() {
      return (
        this.$route.path == "/silentRenewCallback" ||
        this.$route.path == "/callback"
      );
    },

    isHomeRoute() {
      return this.$route.path === "/";
    },

    ...mapState("assetxpstate", ["inventoryActive"]),
  },
  props: {
    source: String,
  },
  mounted: function() {
    if (this.isInIFrame()) {
      debugLog("I'm in an iframe, therefore not continuing");
      return;
    }

    this.checkAuthAndRedirect();
    window.addEventListener("pageshow", this.onPageShow);
    window.addEventListener("workspace-changed", this.onWorkspaceChangedExternally);
    window.addEventListener("workspace-context-changed", this.onWorkspaceContextChanged);
    this.$nextTick(() => this.applyWorkspaceCrossAppSync());
  },
  beforeDestroy() {
    window.removeEventListener("pageshow", this.onPageShow);
    window.removeEventListener("workspace-changed", this.onWorkspaceChangedExternally);
    window.removeEventListener("workspace-context-changed", this.onWorkspaceContextChanged);
    import("@/services/workspaceCrossAppSync").then((m) =>
      m.stopWorkspaceCrossAppSync()
    );
  },
  watch: {
    loggedIn() {
      this.applyWorkspaceCrossAppSync();
      // Ensure markets are loaded when user logs in or workspace changes
      if (this.loggedIn && !this.isInIFrame()) {
        this.$store.dispatch("assetxpstate/GetMyMarkets").catch((err) =>
          console.warn("[App] Failed to load markets:", err)
        );
      }
    },
    "$route.path"() {
      this.applyWorkspaceCrossAppSync();
    },
  },
  data: () => ({
    drawer: null,
    contextWarningOpen: false,
    contextWarningMessage:
      "Your workspace or market selection has changed in another app.",
    lastContextWarningSignature: null,
    lastContextWarningAtMs: 0,
    // Serialisation lock: prevent concurrent workspace-changed handlers from interleaving.
    onWorkspaceChangedExternallyInFlight: false,
    // Per-tenant dedup: skip re-processing the same workspace within 10 s.
    lastWorkspaceChangedTenantId: null,
    lastWorkspaceChangedAtMs: 0,
  }),
  methods: {
    ...mapActions("authentication", ["callLoginOnUm"]),
    /**
     * Another app/tab switched workspace or /ping requested refresh — reload tenant data without full page reload.
     */
    async onWorkspaceChangedExternally(event) {
      if (!this.loggedIn || this.isInIFrame()) return;

      // Serialise: only one workspace-changed handler in flight at a time.
      if (this.onWorkspaceChangedExternallyInFlight) {
        debugLog('[workspace-changed] skipping — workspace reload already in flight');
        return;
      }

      const detail = event && event.detail;
      const incomingTenantId = (detail && detail.selectedTenantId && String(detail.selectedTenantId).trim()) || '';

      // Per-tenant dedup: if we just processed a workspace-changed for the same tenant, skip.
      const nowMs = Date.now();
      if (
        incomingTenantId &&
        incomingTenantId === this.lastWorkspaceChangedTenantId &&
        nowMs - this.lastWorkspaceChangedAtMs < 10000
      ) {
        debugLog('[workspace-changed] deduping same tenant within 10 s:', incomingTenantId);
        return;
      }
      if (incomingTenantId) {
        this.lastWorkspaceChangedTenantId = incomingTenantId;
        this.lastWorkspaceChangedAtMs = nowMs;
      }

      this.onWorkspaceChangedExternallyInFlight = true;
      try {
        const selectedTenantId = incomingTenantId || (detail && detail.selectedTenantId);
        const incomingMarketId =
          detail && typeof detail.selectedMarketId === "string"
            ? detail.selectedMarketId.trim()
            : "";
        let shouldNavigateHome = true;

        if (selectedTenantId != null) {
          const next = String(selectedTenantId).trim();
          const current =
            this.$store.state.workspace &&
            this.$store.state.workspace.currentTenantId != null
              ? String(this.$store.state.workspace.currentTenantId).trim()
              : "";
          if (current !== "" && next === current) {
            debugLog(
              "[workspace-changed] already on workspace",
              next,
              "— still refreshing market/search state"
            );
            shouldNavigateHome = false;
          }
          debugLog("[workspace-changed] switching to", selectedTenantId);
        }
        try {
          debugLog("[MarketTrace][App] workspace-changed start", {
            selectedTenantId,
            currentTenantId:
              this.$store.state.workspace && this.$store.state.workspace.currentTenantId,
            route: this.$route && this.$route.path,
          });

        await this.$store.dispatch("authentication/updateLoggedInStatus");
        debugLog("[MarketTrace][App] auth refreshed", {
          selected_tenant:
            this.$store.state.authentication.user?.profile?.selected_tenant,
          marketAccess:
            this.$store.state.authentication.user?.profile?.["ixp_markets.access"],
          defaultMarket:
            this.$store.state.authentication.user?.profile?.["ixp_markets.defaultmarket"],
        });

        await this.$store.dispatch("workspace/loadWorkspace");
        await this.$store.dispatch("assetxpstate/selectMarket", null);
        await this.$store.dispatch(
          "assetxpstate/ClearSectionAndSearchStateForWorkspaceSwitch"
        );
        await this.$store.dispatch("assetxpstate/GetMyMarkets");

        // Apply incoming event market only when local selection is still empty.
        // Do NOT use auth profile.selected_market here; it can be stale during cross-app transitions.
        const preferredMarketId = incomingMarketId;
        const availableMarketIds = (this.$store.state.assetxpstate.markets || [])
          .map((m) => (m && m.id ? String(m.id).trim() : ""))
          .filter(Boolean);
        const currentSelectedMarketId =
          this.$store.state.assetxpstate &&
          typeof this.$store.state.assetxpstate.selectedMarketId === "string"
            ? this.$store.state.assetxpstate.selectedMarketId.trim()
            : "";
        if (
          !currentSelectedMarketId &&
          preferredMarketId &&
          availableMarketIds.includes(preferredMarketId)
        ) {
          this.$store.commit("assetxpstate/SetSelectedMarket", {
            marketId: preferredMarketId,
            source: "app-workspace-event",
          });
          debugLog("[MarketTrace][App] applied incoming event market", {
            preferredMarketId,
            availableMarketIds,
          });
        }

        await this.$store.dispatch("authentication/updateMarketPermissions");
        debugLog("[MarketTrace][App] workspace-changed complete", {
          workspaceTenant:
            this.$store.state.workspace && this.$store.state.workspace.currentTenantId,
          selectedMarketId: this.$store.state.assetxpstate.selectedMarketId,
          markets: (this.$store.state.assetxpstate.markets || []).map((m) => m && m.id),
          effectiveMarketId: this.$store.getters["assetxpstate/effectiveMarketId"],
        });
        if (shouldNavigateHome && this.$route.path !== "/") {
          this.$router.push("/").catch(() => {});
        }
        } catch (e) {
          console.warn("[App] workspace-changed refetch failed:", e);
        }
      } finally {
        this.onWorkspaceChangedExternallyInFlight = false;
      }
    },
    onWorkspaceContextChanged(event) {
      if (!this.loggedIn || this.isInIFrame()) return;
      const detail = (event && event.detail) || {};
      
      if (process.env.NODE_ENV === 'development') {
        debugLog('[context-changed] Event received:', detail);
      }
      
      const workspaceChanged = !!detail.workspaceChanged;
      const marketChanged = !!detail.marketChanged;
      const source = detail.source || "unknown";
      const selectedTenantId = detail.selectedTenantId || "";
      const selectedMarketId = detail.selectedMarketId || "";
      const currentWorkspaceTenantId =
        this.$store.state.workspace && this.$store.state.workspace.currentTenantId != null
          ? String(this.$store.state.workspace.currentTenantId).trim()
          : "";
      const currentSelectedMarketId =
        this.$store.state.assetxpstate && typeof this.$store.state.assetxpstate.selectedMarketId === "string"
          ? this.$store.state.assetxpstate.selectedMarketId.trim()
          : "";

      // Suppress self-echo workspace notifications immediately after local workspace switch.
      if (
        workspaceChanged &&
        source === "ping" &&
        detail.externalContext === true &&
        detail.appSource === "assetxp"
      ) {
        try {
          const raw = sessionStorage.getItem("assetxp_local_workspace_switch");
          if (raw) {
            const marker = JSON.parse(raw);
            const markerToTenant =
              marker && marker.toTenantId ? String(marker.toTenantId).trim() : "";
            const markerFromTenant =
              marker && marker.fromTenantId ? String(marker.fromTenantId).trim() : "";
            const markerAtMs = marker && Number(marker.atMs) > 0 ? Number(marker.atMs) : 0;
            if (
              markerAtMs > 0 &&
              Date.now() - markerAtMs < 20000 &&
              selectedTenantId &&
              (
                selectedTenantId === markerToTenant ||
                selectedTenantId === markerFromTenant ||
                selectedTenantId === currentWorkspaceTenantId
              )
            ) {
              return;
            }
            if (
              markerAtMs > 0 &&
              Date.now() - markerAtMs < 20000 &&
              !selectedTenantId &&
              currentWorkspaceTenantId &&
              (currentWorkspaceTenantId === markerToTenant ||
                currentWorkspaceTenantId === markerFromTenant)
            ) {
              return;
            }
          }
        } catch (e) {
          // Ignore parsing/storage failures; continue normal flow.
        }
      }

      // Suppress self-echo market notifications shortly after local market selection.
      if (
        marketChanged &&
        source === "poll" &&
        detail.appSource === "assetxp" &&
        selectedMarketId
      ) {
        try {
          const raw = sessionStorage.getItem("assetxp_local_market_change");
          if (raw) {
            const marker = JSON.parse(raw);
            const markerTenant = marker && marker.tenantId ? String(marker.tenantId).trim() : "";
            const markerMarket = marker && marker.marketId ? String(marker.marketId).trim() : "";
            const markerAtMs = marker && Number(marker.atMs) > 0 ? Number(marker.atMs) : 0;
            if (
              markerAtMs > 0 &&
              Date.now() - markerAtMs < 12000 &&
              markerMarket &&
              markerMarket === selectedMarketId &&
              currentSelectedMarketId &&
              currentSelectedMarketId === selectedMarketId &&
              currentWorkspaceTenantId &&
              markerTenant === currentWorkspaceTenantId
            ) {
              return;
            }
          }
        } catch (e) {
          // Ignore parsing/storage failures; continue normal flow.
        }
      }

      // Show popup for any context change (workspace or market)
      // For workspace: only show if it came from external detection (broadcast/storage/poll from other origin)
      // For market: always show when detected (could be from polling when other app changed it)
      if (workspaceChanged) {
        // Initial login/hydration can emit workspace context events before the app has a
        // previous workspace loaded. Do not show a "workspace changed" warning in that case.
        if (!currentWorkspaceTenantId) return;

        // Also suppress workspace warnings when the incoming tenant matches the current tenant
        // and there is no market delta (same-workspace refresh, not an actual switch).
        if (
          selectedTenantId &&
          selectedTenantId === currentWorkspaceTenantId &&
          !marketChanged
        ) {
          return;
        }

        const isExternalWorkspaceEvent = 
          detail.source === 'broadcast' || 
          detail.source === 'storage' ||
          detail.externalContext === true;
        if (!isExternalWorkspaceEvent) return;
      }

      // BUG FIX: De-duplicate repeated notifications with identical payload (12s window)
      // Include appSource in signature to prevent cross-app dedup interference
      const appSource = detail.appSource || "unknown";
      const signature = [
        workspaceChanged ? "w1" : "w0",
        marketChanged ? "m1" : "m0",
        source,
        appSource,
        selectedTenantId,
        selectedMarketId,
      ].join("|");
      const now = Date.now();
      if (
        this.lastContextWarningSignature === signature &&
        now - this.lastContextWarningAtMs < 12000
      ) {
        return;
      }
      this.lastContextWarningSignature = signature;
      this.lastContextWarningAtMs = now;
      
      this.showContextWarning(workspaceChanged, marketChanged);
    },
    showContextWarning(workspaceChanged, marketChanged) {
      if (workspaceChanged && marketChanged) {
        this.contextWarningMessage =
          "Your workspace and market selections changed in another app.";
      } else if (workspaceChanged) {
        this.contextWarningMessage = "Your workspace changed in another app.";
      } else if (marketChanged) {
        this.contextWarningMessage = "Your market changed in another app.";
      } else {
        this.contextWarningMessage =
          "Your workspace or market selection has changed in another app.";
      }
      this.contextWarningOpen = true;
    },
    applyWorkspaceCrossAppSync() {
      // Temporary kill switch (see AppBar.vue's hideWorkspaceAndMarketSelectors for the matching
      // half, and docs/workspace-market-switching-kill-switch.md): stops the /ping poller and the
      // BroadcastChannel/localStorage cross-tab listeners from ever starting while workspace/
      // market switching is hard-disabled in production. Safe today because no production account
      // has more than one workspace/market to switch between, so there's nothing for this loop to
      // react to. To re-enable, restore the code this replaced (see git history for this file)
      // and remove this comment.
      return;
    },
    isInIFrame() {
      try {
        return window.self !== window.top;
      } catch (e) {
        return true;
      }
    },
    async onPageShow(event) {
      if (event.persisted) {
        await this.$store.dispatch("authentication/updateLoggedInStatus");
        this.$nextTick(() => this.checkAuthAndRedirect());
      }
    },
    checkAuthAndRedirect() {
      debugLog("Checking if user is logged in", this.$route.path);
      if (!this.loggedIn) {
        if (
          this.$route.path != "/silentRenewCallback" &&
          this.$route.path != "/callback" &&
          this.$route.path != "/noAccess"
        ) {
          debugLog("User is not logged in, redirecting");
          const returnUrl = this.$route.fullPath || this.$route.path;
          this.callLoginOnUm(returnUrl);
        } else {
          debugLog("Silent Refresh or callback page", this.$route);
          debugLog("Page", window.location.href);
        }
      } else {
        debugLog("User IS logged in");
      }
    },
  },
};
</script>

<style lang="scss">
@use "@/styles/main.scss";

html {
  overflow-y: auto !important;
}

.fade-enter-active,
.fade-leave-active {
  transition-property: opacity;
  transition-duration: 0.15s;
  overflow: hidden;
}

.fade-enter-active {
  transition-delay: 0.15s;
}

.fade-leave-active {
  opacity: 0;
}

.fade-enter {
  opacity: 0;
}

#AssetXP .context-warning-card {
  box-shadow: inset 4px 0 0 #064287;
  border-radius: 10px;
  overflow: hidden;
}

#AssetXP .context-warning-title {
  background: linear-gradient(135deg, #064287 0%, #084da8 100%);
  color: white;
  font-weight: 600;
  padding: 16px 24px 16px 42px;
  font-size: 16px;
}

#AssetXP .context-warning-title .context-warning-icon {
  color: #fff !important;
  margin-left: 6px !important;
}

#AssetXP .context-warning-text {
  color: #333;
  font-size: 14px;
  line-height: 1.5;
  padding: 20px 28px;
}

#AssetXP .context-warning-btn,
#AssetXP .context-warning-btn .v-btn__content {
  color: #fff !important;
}
</style>
